import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import {
  downloadPerformaPaymentReceipt,
  getAccountPaidBillsReports
} from '../services/api/implementation/impl';
import Loader from '../../shared/components/ui/Loader';
import PaginationComponent from '../../shared/components/PaginationComponent';
import {
  EmptyRow,
  FilterBar,
  Table,
  TableCard,
  TBody,
  Td,
  Th,
  THead,
  Tile,
  TileRow
} from '../../shared/components/ui/primitives';
import { money, shortDate } from './SiteWallet/siteWalletUi';
import { PdfButton } from './PerformaInvoiceParts';
import {
  BillsFilterFields,
  KIND_TEXT,
  KindPill,
  useBillsFilters,
  useCsvButton
} from './billsReportUi';

const PAGE_SIZE = 25;

const MODE_LABEL = {
  NEFT: 'NEFT',
  RTGS: 'RTGS',
  IMPS: 'IMPS',
  UPI: 'UPI',
  CHEQUE: 'Cheque',
  CASH: 'Cash',
  BANK: 'Bank'
};
const modeText = (mode) => (mode ? (MODE_LABEL[mode] ?? mode) : '—');

/**
 * Every payment that went out — vendor PIs, service providers, labour wages,
 * wallet reimbursements — one row each, newest first, for matching against
 * the bank statement.
 */
const AccountsPaidBills = () => {
  const userId = Cookies.get('userId');
  const { filters, set, typed, setTyped, filtered, clear } = useBillsFilters();
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [filters]);

  const paid = useQuery({
    queryKey: ['accountPaidBills', page, filters],
    queryFn: () => getAccountPaidBillsReports({ ...filters, page, limit: PAGE_SIZE }),
    keepPreviousData: true
  });
  const rows = paid.data?.data ?? [];
  const meta = paid.data?.meta ?? {};
  const totals = meta.totals ?? {};
  const byKind = totals.by_kind ?? {};
  const totalPages = meta.total ? Math.ceil(meta.total / PAGE_SIZE) : 1;

  useEffect(() => {
    mixpanel.track('Account Paid Bills Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useCsvButton({
    fileName: 'paid_bills.csv',
    fetchAll: async () =>
      (await getAccountPaidBillsReports({ ...filters, page: 1, limit: 1000 }))?.data ?? [],
    toLine: (r) => ({
      Date: shortDate(r.date),
      'Paid to': r.paid_to ?? '',
      Against: r.against,
      Project: r.project ?? 'No project',
      Type: KIND_TEXT[r.kind] ?? r.kind,
      Mode: modeText(r.mode),
      'UTR / reference': r.reference ?? '',
      Amount: r.amount
    })
  });

  return (
    <div className="flex w-full flex-col gap-4 bg-gray-100 px-3 py-4">
      <Loader isVisible={paid.isFetching && !paid.data} />

      <TileRow>
        <Tile
          variant="card"
          label="Total paid"
          hint={`${totals.count ?? 0} payment${totals.count === 1 ? '' : 's'}${filtered ? ' for these filters' : ''}`}
          value={money(totals.amount ?? 0)}
          tone="success"
        />
        <Tile
          variant="card"
          label="Material"
          hint="Vendor PIs"
          value={money(byKind.material ?? 0)}
        />
        <Tile
          variant="card"
          label="Service providers"
          hint="Task and running bills"
          value={money(byKind.service_providers ?? 0)}
        />
        <Tile
          variant="card"
          label="Labour + wallet"
          hint="Wages, advances, reimbursements"
          value={money((byKind.labour ?? 0) + (byKind.site ?? 0))}
        />
      </TileRow>

      <FilterBar
        end={
          filtered && (
            <button
              type="button"
              onClick={clear}
              className="mb-2 text-sm font-semibold text-brand-700 hover:underline"
            >
              Clear
            </button>
          )
        }
      >
        <BillsFilterFields
          filters={filters}
          set={set}
          typed={typed}
          setTyped={setTyped}
          placeholder="Payee, PI, bill or UTR"
        />
      </FilterBar>

      {paid.isError ? (
        <p className="text-sm text-danger-fg">
          Couldn&apos;t load paid bills:{' '}
          {paid.error?.response?.data?.message || paid.error?.message}
        </p>
      ) : (
        <TableCard>
          <Table>
            <THead>
              <tr>
                <Th>Date</Th>
                <Th>Paid to</Th>
                <Th>Against</Th>
                <Th>Project</Th>
                <Th>Type</Th>
                <Th>Mode</Th>
                <Th>UTR / reference</Th>
                <Th align="right">Amount</Th>
                <Th align="right">Receipt</Th>
              </tr>
            </THead>
            <TBody>
              {!rows.length && !paid.isFetching && (
                <EmptyRow colSpan={9}>
                  No payments{filtered ? ' for these filters' : ' yet'}.
                </EmptyRow>
              )}
              {rows.map((r) => (
                <tr key={r.key}>
                  <Td className="whitespace-nowrap text-gray-600">{shortDate(r.date)}</Td>
                  <Td>{r.paid_to ?? '—'}</Td>
                  <Td className="whitespace-nowrap text-gray-700">{r.against}</Td>
                  <Td className={r.project ? 'text-gray-600' : 'italic text-gray-500'}>
                    {r.project ?? 'No project'}
                  </Td>
                  <Td>
                    <KindPill kind={r.kind} />
                  </Td>
                  <Td className="text-gray-600">{modeText(r.mode)}</Td>
                  <Td className="text-gray-600">{r.reference || '—'}</Td>
                  <Td align="right" numeric className="font-medium">
                    {money(r.amount)}
                  </Td>
                  <Td align="right" className="whitespace-nowrap">
                    {r.receipt_id ? (
                      <PdfButton
                        compact
                        label="Receipt"
                        onDownload={() => downloadPerformaPaymentReceipt(r.receipt_id)}
                      />
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </Td>
                </tr>
              ))}
            </TBody>
          </Table>
        </TableCard>
      )}

      {totalPages > 1 && (
        <PaginationComponent
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          isFetching={paid.isFetching}
        />
      )}
    </div>
  );
};

export default AccountsPaidBills;
